/**
 * Drivstofftype, optionally broken down by brand (#586 AC5).
 *
 * With no brand selected this is the plain three-column table the tab has
 * always shown. Select brands and it becomes a matrix: one row per fuel, one
 * column group per series, "Alle" first.
 *
 * Every column's Andel % is of its OWN total, so each column sums to 100. That
 * is what makes a 400-car brand comparable against a 100 000-car segment —
 * sharing one denominator would flatten the small brand into rounding noise.
 *
 * Fuel row order comes from the "Alle" series, so rows line up across columns.
 */
import { useTranslation } from 'react-i18next'
import { fuelOrder, type DrivstoffSerie } from './drivstoffRows'

interface Props {
  series: DrivstoffSerie[]
}

const EM_DASH = '—'
const fmtCount = (n: number) => n.toLocaleString('nb-NO')
const fmtPct = (n: number) =>
  n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const th = 'px-3 py-2 whitespace-nowrap'

export default function DrivstoffMatrise({ series }: Props) {
  const { t } = useTranslation('bruktbil')
  if (series.length === 0 || series[0].rows.length === 0) {
    return <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">{t('common.ingenData')}</div>
  }

  const fuels = fuelOrder(series)
  const byMerke = series.map(s => ({
    merke: s.merke,
    total: s.total,
    rows: new Map(s.rows.map(r => [r.drivstoff, r])),
  }))

  return (
    <div className="card overflow-x-auto" data-testid="drivstoff-matrise">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
          <tr>
            <th className={th} rowSpan={2}>{t('drivstoff.drivstoff')}</th>
            {byMerke.map(m => (
              <th key={m.merke} className={`${th} text-center border-l border-gray-200 dark:border-gray-700`} colSpan={2}>
                {m.merke === 'Alle' ? t('common.alle') : m.merke}
              </th>
            ))}
          </tr>
          <tr>
            {byMerke.map(m => [
              <th key={`${m.merke}-n`} className={`${th} text-right border-l border-gray-200 dark:border-gray-700 font-normal`}>
                {t('common.antall')}
              </th>,
              <th key={`${m.merke}-p`} className={`${th} text-right font-normal`}>
                {t('common.andelPct')}
              </th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {fuels.map((fuel, i) => (
            <tr
              key={fuel}
              data-testid="drivstoff-row"
              className={i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}
            >
              <td className="px-3 py-1.5">{fuel}</td>
              {byMerke.map(m => {
                const row = m.rows.get(fuel)
                return [
                  <td
                    key={`${m.merke}-${fuel}-n`}
                    className="px-3 py-1.5 tabular-nums text-right border-l border-gray-200 dark:border-gray-700"
                  >
                    {fmtCount(row?.count ?? 0)}
                  </td>,
                  <td key={`${m.merke}-${fuel}-p`} className="px-3 py-1.5 tabular-nums text-right">
                    {row ? fmtPct(row.share_pct) : EM_DASH}
                  </td>,
                ]
              })}
            </tr>
          ))}
          <tr
            data-testid="drivstoff-total"
            className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-primary-300 dark:border-primary-600"
          >
            <td className="px-3 py-1.5">{t('common.sumTotal')}</td>
            {byMerke.map(m => [
              <td
                key={`${m.merke}-total-n`}
                className="px-3 py-1.5 tabular-nums text-right border-l border-gray-200 dark:border-gray-700"
              >
                {fmtCount(m.total)}
              </td>,
              <td key={`${m.merke}-total-p`} className="px-3 py-1.5 tabular-nums text-right">
                {m.total > 0 ? '100,0' : EM_DASH}
              </td>,
            ])}
          </tr>
        </tbody>
      </table>
    </div>
  )
}
